import api from "./api";
import axios from "./axios";

export const OPTION_ROWS_END_POINT = "/option-rows";
const OTHER_INFO_END_POINT = `${OPTION_ROWS_END_POINT}/other-info`;
const TENORS_END_POINT = `${OPTION_ROWS_END_POINT}/tenors`;
const UPDATE_OPTION_END_POINT = `${OPTION_ROWS_END_POINT}/update`;
const DUPLICATE_OPTION_END_POINT = `${OPTION_ROWS_END_POINT}/bulk-duplicate`;
const DELETE_OPTION_END_POINT = `${OPTION_ROWS_END_POINT}/bulk-delete`;
const ACTIVE_OPTION_END_POINT = `${OPTION_ROWS_END_POINT}/active`;
const UPDATE_LIVE_END_POINT = `${OPTION_ROWS_END_POINT}/update`;
const UPDATE_LOCK_END_POINT = `${OPTION_ROWS_END_POINT}/update`;
const UPDATE_AUTO_UPDATE_VOL_END_POINT = `${OPTION_ROWS_END_POINT}/update-auto-update-vol`;
const TEAM_RUN_END_POINT = "/team-run";
const PUSH_CHECK_END_POINT = `${TEAM_RUN_END_POINT}/push-check`;
const PUSH_TO_TEAM_RUN_END_POINT = `${TEAM_RUN_END_POINT}/push`;
const PUSH_TO_AUDIT_TRAIL_END_POINT = `${OPTION_ROWS_END_POINT}/push-to-audit-trail`;
const OPTION_TYPES_END_POINT = "/option-types";
const SHORTCUTS_END_POINT = "add-options/shortcuts";
const PRODUCTS_END_POINT = "/products";
const CONTRACT_STYLE_END_POINT = "/contracts-styles";
const CONTRACT_STYLE_TENORS_END_POINT = `product-contract-styles/tenors`;
const BESPOKE_TENORS_END_POINT = "bespoke-tenors-by-pcs-id"; //"/bespoke-tenors";
const CONSTRAINTS_END_POINT = "constraints-by-pcs-id"; //"/constraints";
export const FEED_TABLES_END_POINT = "/trade-feed";

export const fetchProductsExists = async () => {
  return await api.get(OPTION_ROWS_END_POINT);
};

export const calculatePayout = async (summaryId, options = {}) => {
  const { ul_settlement_price, side, popup_session } = options;
  const query = { ul_settlement_price };
  const body = {};
  if (side != null && String(side).trim() !== "") {
    body.side = side;
  }
  if (typeof popup_session === "string" && popup_session.trim() !== "") {
    const token = popup_session.trim();
    query.popup_session = token;
    body.ul_settlement_price = ul_settlement_price;
    body.popup_session = token;
  }

  return await api.post(
    `${OPTION_ROWS_END_POINT}/${summaryId}/payout/calculate`,
    query,
    body
  );
};

export const fetchPayoutData = async (summaryId, body) => {
  return await api.get(`${OPTION_ROWS_END_POINT}/${summaryId}/payout`, {
    ...body,
  });
};

/**
 * Strategy Lab / payout analysis curves (one metric per request).
 * @param {number|string} summaryId
 * @param {object} [params] — see `buildPayoutAnalysisQueryParams` in payoutAnalysisApi.js
 * @returns {Promise<import('axios').AxiosResponse<{ message: string, data: object }>>}
 */
export const fetchPayoutAnalysis = async (summaryId, params = {}) => {
  return await api.get(
    `${OPTION_ROWS_END_POINT}/${summaryId}/payout/analysis`,
    { ...params }
  );
};

/**
 * Bulk P&L overlay (2–10 unique summary ids). No metric tabs.
 * POST `{ ids, ...knobs }` — GET with `ids[]` still works on the backend.
 * @see docs/features/pricer/payout-compare-frontend.md
 */
export const fetchPayoutCompare = async (params = {}) => {
  return await api.post(`${OPTION_ROWS_END_POINT}/payout/compare`, {}, params);
};

export const fetchOtherInfoData = async (summaryId) => {
  return await api.get(`${OTHER_INFO_END_POINT}?summary_id=${summaryId}`);
};

/**
 * Admin-only pricing source + calculation steps for one pricing target/side.
 * Query: side, optional calculation_date, optional pricing_row_id (subleg_id alias).
 * @see docs/features/pricer/show-formulas-frontend.md
 */
export const fetchCalculationFormulas = async (summaryId, params = {}) => {
  return await api.get(
    `${OPTION_ROWS_END_POINT}/${summaryId}/calculation-formulas`,
    { ...params }
  );
};

export const updateAdditionalInfoField = async (summaryId, key, value) => {
  return await api.put(
    `${OPTION_ROWS_END_POINT}/${summaryId}/additional-info`,
    {},
    { key, value }
  );
};
export const fetchTenors = async (summaryId, legId) => {
  return await api.get(
    `${TENORS_END_POINT}?summary_id=${summaryId}&leg_id=${legId}`
  );
};

/**
 * Missing labels and unexpired existing tenors for the Add Tenor popup.
 * @see docs/features/pricer/tenor-creation-frontend.md
 */
export const fetchMissingTenors = async ({
  product_id,
  contract_id,
  style_id,
  period_id,
}) => {
  return await api.get(`${TENORS_END_POINT}/missing`, {
    product_id,
    contract_id,
    style_id,
    period_id,
  });
};

/**
 * PCS cascade options + single-choice defaults for the Add Tenor popup.
 * @see docs/features/pricer/tenor-creation-frontend.md
 */
export const fetchTenorFormContext = async ({
  product_id,
  contract_id,
  style_id,
}) => {
  const params = { product_id };
  if (contract_id != null) params.contract_id = contract_id;
  if (style_id != null) params.style_id = style_id;
  return await api.get(`${TENORS_END_POINT}/form-context`, params);
};

/**
 * Create or update style_period_tenors + month rows for Add Option.
 * Identity is product + contract + style + period + tenor.
 * @see docs/features/pricer/tenor-creation-frontend.md
 */
export const createOptionRowTenor = async (body) => {
  return await api.post(TENORS_END_POINT, {}, body);
};

/**
 * Create or update multiple catalog tenors for the same PCS in one request.
 * @see docs/features/pricer/tenor-creation-frontend.md
 */
export const batchCreateOptionRowTenors = async (body) => {
  return await api.post(`${TENORS_END_POINT}/batch`, {}, body);
};

export const updateOnEditCell = async (body) => {
  return await api.post(UPDATE_OPTION_END_POINT, { ...body });
};

export const duplicateRow = async (summary_ids) => {
  return await api.post(DUPLICATE_OPTION_END_POINT, { ids: summary_ids });
};

export const deleteRow = async (summary_ids) => {
  return await api.delete(DELETE_OPTION_END_POINT, { ids: summary_ids });
};

/** Park / unpark Pricer options. Same shape as Team Run `POST /team-run/active`. */
export const updateOptionRowsActiveStatus = async (summaryIds, isActive) => {
  return await api.post(ACTIVE_OPTION_END_POINT, {
    summary_ids: summaryIds,
    is_active: isActive,
  });
};

export const updateLiveOrUpdateTraderRow = async (field, body) => {
  var updateEndpoint = UPDATE_LIVE_END_POINT;
  if (field === "is_auto_update_vol") {
    updateEndpoint = UPDATE_AUTO_UPDATE_VOL_END_POINT;
  }
  return await api.post(updateEndpoint, { ...body });
};

export const updateLock = async (body) => {
  return await api.post(UPDATE_LOCK_END_POINT, { ...body });
};

export const triggerPushOptionToPricer = async (body) => {
  return await api.post(PUSH_CHECK_END_POINT, { ...body });
};

export const onPushOptionClicked = async (body) => {
  return await api.post(PUSH_TO_TEAM_RUN_END_POINT, {}, { ...body });
};

export const triggerPushOptionToAuditTrailAPI = async (body) => {
  return await api.post(PUSH_TO_AUDIT_TRAIL_END_POINT, body);
};

export const fetchTypes = async (legsNum) => {
  return await api.get(`${OPTION_TYPES_END_POINT}?number_of_legs=${legsNum}`);
};

export const fetchCommoditiesAPI = async () => {
  return await api.get(SHORTCUTS_END_POINT);
};

export const fetchProductsAPI = async () => {
  return await api.get(PRODUCTS_END_POINT);
};

export const fetchContractStylesAPI = async (productId) => {
  return await api.get(
    `${PRODUCTS_END_POINT}/${productId}${CONTRACT_STYLE_END_POINT}`
  );
};

export const fetchTenorAPI = async (contractStyleSelected) => {
  return await api.get(
    `${CONTRACT_STYLE_TENORS_END_POINT}?product_contract_style_id=${contractStyleSelected}`
  );
};

export const fetchBespokeTenorAPI = async (contractStyleSelected, periodId = 1) => {
  return await api.get(
    `${BESPOKE_TENORS_END_POINT}?product_contract_style_id=${contractStyleSelected}&period_id=${periodId}`
  );
};

export const fetchConstraintsAPI = async (typeId, legsNum, contract_style) => {
  return await api.get(
    `${CONSTRAINTS_END_POINT}?option_type_id=${typeId}&number_of_legs=${legsNum}&product_contract_style_id=${contract_style}`
  );
};

export const submitAddOptionAPI = async (body) => {
  return await api.post(OPTION_ROWS_END_POINT, {}, { ...body });
};

const IMPORT_CSV_END_POINT = `${OPTION_ROWS_END_POINT}/import-csv`;

export const importCSVFile = async (formData) => {
  // Use axios directly for FormData
  // Transform request to remove Content-Type header for FormData (browser will set it with boundary)
  return await axios.post(IMPORT_CSV_END_POINT, formData, {
    transformRequest: [
      (data, headers) => {
        // Remove Content-Type header for FormData - browser will set it automatically
        if (data instanceof FormData) {
          delete headers["Content-Type"];
        }
        return data;
      },
    ],
  });
};
